// API服务，用于与服务器端通信

import { clearAuthScope, getAuthScope, getToken, isAdminPath, replaceToken } from '../utils/authStorage';

const API_BASE_URL = '/api';

export class ApiError extends Error {
  status: number;
  code?: string;
  mustChangePassword?: boolean;
  constructor(message: string, status: number, extra?: { code?: string; mustChangePassword?: boolean }) {
    super(message);
    this.status = status;
    this.code = extra?.code;
    this.mustChangePassword = extra?.mustChangePassword;
  }
}

type MustChangeHandler = () => void;
let onMustChangePassword: MustChangeHandler | null = null;
export function setMustChangePasswordHandler(handler: MustChangeHandler | null) {
  onMustChangePassword = handler;
}

/** 闲置过期：清当前作用域并跳转登录页 */
function handleUnauthorized() {
  const scope = getAuthScope();
  clearAuthScope(scope);
  const loginPath = isAdminPath() ? '/login' : '/login';
  if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
    window.location.href = loginPath;
  }
}

// 通用请求函数
async function request<T>(endpoint: string, options: RequestInit & { params?: Record<string, any> } = {}): Promise<T> {
  const token = getToken();
  
  // 处理查询参数
  let url = `${API_BASE_URL}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const paramsString = searchParams.toString();
    if (paramsString) {
      url += (url.includes('?') ? '&' : '?') + paramsString;
    }
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const { params, ...fetchOptions } = options;
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // 滑动续期：服务端下发新 token 时替换本地
  const newToken = response.headers.get('X-New-Token');
  if (newToken) {
    replaceToken(newToken);
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as {
      error?: string;
      code?: string;
      mustChangePassword?: boolean;
    };
    if (response.status === 401) {
      handleUnauthorized();
    }
    if (
      response.status === 403 &&
      (errorData.code === 'MUST_CHANGE_PASSWORD' || errorData.mustChangePassword)
    ) {
      onMustChangePassword?.();
    }
    throw new ApiError(errorData.error || '请求失败', response.status, {
      code: errorData.code,
      mustChangePassword: errorData.mustChangePassword,
    });
  }
  
  return response.json();
}

// 认证相关API
export const authAPI = {
  // 注册
  register: async (email: string, password: string, name: string) => {
    return request<{ user: any; token: string; mustChangePassword?: boolean }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  },
  
  // 登录
  login: async (email: string, password: string) => {
    return request<{ user: any; token: string; mustChangePassword?: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  // 修改自己的密码
  changePassword: async (oldPassword: string, newPassword: string) => {
    return request<{ success: boolean; token: string; mustChangePassword: boolean; user: any }>(
      '/auth/change-password',
      {
        method: 'POST',
        body: JSON.stringify({ oldPassword, newPassword }),
      }
    );
  },
  
  // 获取当前用户
  getCurrentUser: async () => {
    const token = getToken();
    if (!token) {
      return null;
    }
    try {
      return request<any>('/auth/me');
    } catch (error) {
      return null;
    }
  },
};

// 积分相关API
export const creditsAPI = {
  // 获取用户积分
  getUserCredits: async (userId: string) => {
    return request<any>(`/credits/${userId}`);
  },
  
  // 扣减用户积分
  deductCredits: async (userId: string, amount: number, description: string, referenceId?: string) => {
    return request<{ success: boolean; newBalance: number }>('/credits/deduct', {
      method: 'POST',
      body: JSON.stringify({ userId, amount, description, referenceId }),
    });
  },
  
  // 增加用户积分
  addCredits: async (userId: string, amount: number, description: string, referenceId?: string) => {
    return request<{ success: boolean; newBalance: number }>('/credits/add', {
      method: 'POST',
      body: JSON.stringify({ userId, amount, description, referenceId }),
    });
  },
  
  // 获取用户积分交易记录
  getUserCreditTransactions: async (userId: string) => {
    return request<any[]>(`/credits/transactions/${userId}`);
  },
};

// 历史记录相关API
export const historyAPI = {
  // 获取用户历史记录（支持分类 / 关键词 / 标签）
  getUserHistory: async (
    userId: string,
    categoryOrOptions?: string | string[] | { category?: string | string[]; limit?: number; q?: string; tag?: string },
    limit: number = 50
  ) => {
    let category: string | string[] | undefined;
    let q: string | undefined;
    let tag: string | undefined;
    let finalLimit = limit;

    if (typeof categoryOrOptions === 'string' || Array.isArray(categoryOrOptions)) {
      category = categoryOrOptions;
    } else if (categoryOrOptions && typeof categoryOrOptions === 'object') {
      category = categoryOrOptions.category;
      q = categoryOrOptions.q;
      tag = categoryOrOptions.tag;
      finalLimit = categoryOrOptions.limit ?? limit;
    }

    let url = `/history/${userId}?limit=${finalLimit}`;
    if (category) {
      const categoryParam = Array.isArray(category) ? category.join(',') : category;
      url += `&category=${encodeURIComponent(categoryParam)}`;
    }
    if (q) url += `&q=${encodeURIComponent(q)}`;
    if (tag) url += `&tag=${encodeURIComponent(tag)}`;
    return request<any[]>(url);
  },
  
  // 创建历史记录
  createHistory: async (data: any) => {
    return request<{ success: boolean; id: string }>('/history', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // 更新历史记录
  updateHistory: async (id: string, data: any) => {
    return request<{ success: boolean }>(`/history/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  // 删除历史记录
  deleteHistory: async (id: string) => {
    return request<{ success: boolean }>(`/history/${id}`, {
      method: 'DELETE',
    });
  },
  
  // 删除多条历史记录
  deleteMultipleHistory: async (ids: string[]) => {
    return request<{ success: boolean }>('/history', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  },
  
  // 通过任务ID更新历史记录
  updateHistoryByTaskId: async (userId: string, taskId: string, data: any) => {
    return request<{ success: boolean }>(`/history/task/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify({ userId, ...data }),
    });
  },
};

// 用户管理相关API
export const userAPI = {
  // 获取所有用户
  getAllUsers: async () => {
    return request<any[]>('/users');
  },
  
  // 禁止/启用用户
  toggleUserStatus: async (id: string, is_active: boolean) => {
    return request<{ success: boolean }>(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    });
  },
  
  // 重置用户密码
  resetUserPassword: async (id: string, newPassword: string) => {
    return request<{ success: boolean }>(`/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  },
  
  // 删除用户
  deleteUser: async (id: string) => {
    return request<{ success: boolean }>(`/users/${id}`, {
      method: 'DELETE',
    });
  },
  
  // 设置用户积分
  setUserCredits: async (id: string, credits: number, description: string) => {
    return request<{ success: boolean }>(`/users/${id}/credits`, {
      method: 'PUT',
      body: JSON.stringify({ credits, description }),
    });
  },
};

// 模型配置相关API
export const modelAPI = {
  // 获取所有模型配置
  getAllModels: async () => {
    return request<any[]>('/models');
  },
  
  // 获取所有模型配置（别名，兼容现有代码）
  getModels: async () => {
    return request<any[]>('/models');
  },
  
  // 获取指定类型的模型配置
  getModelsByType: async (type: string) => {
    return request<any[]>(`/models/type/${type}`);
  },
  
  // 创建模型配置
  createModel: async (data: any) => {
    return request<{ success: boolean; id: string }>('/admin/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // 更新模型配置
  updateModel: async (id: string, data: any) => {
    return request<{ success: boolean }>(`/admin/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // 删除模型配置
  deleteModel: async (id: string) => {
    return request<{ success: boolean }>(`/admin/models/${id}`, {
      method: 'DELETE',
    });
  },
  
  // 切换模型状态
  toggleModelStatus: async (id: string, is_active: boolean) => {
    return request<{ success: boolean }>(`/models/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    });
  },

  // 按品牌整体启用/停用（启用时关闭其它品牌）
  toggleProviderStatus: async (provider: string, is_active: boolean) => {
    return request<{ success: boolean }>(`/admin/providers/${provider}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    });
  },
};

// 虚拟模特相关API
export const virtualModelAPI = {
  // 获取用户的虚拟模特列表
  getUserVirtualModels: async (userId: string) => {
    return request<any[]>(`/virtual-models/${userId}`);
  },
  
  // 创建虚拟模特
  createVirtualModel: async (data: any) => {
    return request<{ success: boolean; id: string }>('/virtual-models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // 删除虚拟模特
  deleteVirtualModel: async (id: string) => {
    return request<{ success: boolean }>(`/virtual-models/${id}`, {
      method: 'DELETE',
    });
  },
};

// 工具积分消耗配置相关API
export const toolCreditCostsAPI = {
  // 获取工具积分消耗配置
  getToolCreditCosts: async () => {
    return request<any[]>('/tool-credit-costs');
  },
};

// 积分套餐相关API
export const packagesAPI = {
  // 获取积分套餐
  getCreditPackages: async () => {
    return request<any[]>('/credit-packages');
  },
};

// 订单相关API
export const orderAPI = {
  // 创建订单
  createOrder: async (userId: string, packageId: string, credits: number, amount: number) => {
    return request<{ success: boolean; orderId?: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify({ userId, packageId, credits, amount }),
    });
  },
  
  // 完成订单
  completeOrder: async (orderId: string, transactionId: string) => {
    return request<{ success: boolean }>(`/orders/${orderId}/complete`, {
      method: 'PUT',
      body: JSON.stringify({ transactionId }),
    });
  },
  
  // 取消订单
  cancelOrder: async (orderId: string) => {
    return request<{ success: boolean }>(`/orders/${orderId}/cancel`, {
      method: 'PUT',
    });
  },

  // 退款订单（回收积分）
  refundOrder: async (orderId: string) => {
    return request<{ success: boolean; refunded?: boolean; balanceAfter?: number }>(`/orders/${orderId}/refund`, {
      method: 'PUT',
    });
  },
  
  // 获取订单详情
  getOrderById: async (orderId: string) => {
    return request<any>(`/orders/${orderId}`);
  },
  
  // 获取用户订单
  getUserOrders: async (userId: string) => {
    return request<any[]>(`/orders/user/${userId}`);
  },
};

// 管理员相关API
export const adminAPI = {
  // 获取所有用户（管理员）
  getAllUsers: async () => {
    return request<any[]>('/admin/users');
  },
  
  // 禁止/启用用户（管理员）
  toggleUserStatus: async (id: string, is_active: boolean) => {
    return request<{ success: boolean }>(`/admin/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    });
  },
  
  // 重置用户密码（管理员）
  resetUserPassword: async (id: string, password: string) => {
    return request<{ success: boolean }>(`/admin/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },

  // 更新用户信息（管理员；本人可改邮箱）
  updateUser: async (id: string, data: { name?: string; role?: string; is_active?: boolean; email?: string }) => {
    return request<{ success: boolean }>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  // 设置用户积分（管理员）
  setUserCredits: async (id: string, credits: number) => {
    return request<{ success: boolean }>(`/admin/users/${id}/credits`, {
      method: 'PUT',
      body: JSON.stringify({ credits }),
    });
  },
  
  // 删除用户（管理员）
  deleteUser: async (id: string) => {
    return request<{ success: boolean }>(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  },
  
  // 获取所有模型配置（管理员）
  getAllModels: async () => {
    return request<any[]>('/admin/models');
  },

  // 测试模型联通（管理员）
  testModel: async (id: string) => {
    return request<{ success: boolean; message?: string }>(`/admin/models/${id}/test`, {
      method: 'POST',
    });
  },
  
  // 创建模型配置（管理员）
  createModel: async (data: any) => {
    return request<{ success: boolean; id: string }>('/admin/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // 更新模型配置（管理员）
  updateModel: async (id: string, data: any) => {
    return request<{ success: boolean }>(`/admin/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  // 删除模型配置（管理员）
  deleteModel: async (id: string) => {
    return request<{ success: boolean }>(`/admin/models/${id}`, {
      method: 'DELETE',
    });
  },

  // 获取积分套餐配置（管理员）
  getCreditPackages: async () => {
    return request<any[]>('/admin/credit-packages');
  },

  // 更新积分套餐（标题 / 价格 / 积分）
  updateCreditPackage: async (id: string, data: { name: string; price: number; credits: number }) => {
    return request<{ success: boolean }>(`/admin/credit-packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  // 获取生成历史记录（管理员）
  getGenerationHistory: async (limit: number = 50, offset: number = 0) => {
    return request<any[]>('/admin/history', {
      params: { limit, offset }
    });
  },
  
  // 获取积分交易记录（管理员）
  getCreditTransactions: async (limit: number = 50, offset: number = 0) => {
    return request<any[]>('/admin/credit-transactions', {
      params: { limit, offset }
    });
  },
  
  // 获取全平台统计数据
  getStats: async () => {
    return request<any>('/admin/stats');
  },
  
  // 获取全平台创作历史
  getAllHistory: async (params: { limit: number; offset: number }) => {
    return request<any[]>('/admin/history', {
      params
    });
  },
  
  // 获取全平台交易记录
  getTransactions: async (params: { limit: number; offset: number }) => {
    return request<any[]>('/admin/credit-transactions', {
      params
    });
  },
  
  // 批量获取用户信息
  getUsersMap: async (userIds: string[]) => {
    return request<Record<string, { name?: string; email?: string }>>('/admin/users/map', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  },

  /** 系统设置：登录态有效期等 */
  getSettings: async () => {
    return request<{ jwt_expires_seconds: number }>('/admin/settings');
  },

  updateSettings: async (data: { jwt_expires_seconds: number }) => {
    return request<{ success: boolean; jwt_expires_seconds: number }>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

/** 阿里 DashScope 业务代理（密钥由后端从启用配置读取） */
export const alibabaAI = {
  chat: async (messages: Array<{ role: string; content: unknown }>, options?: { max_tokens?: number; model_id?: string }) => {
    return request<any>('/ai/alibaba/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, ...options }),
    });
  },
  image: async (payload: { prompt: string; size?: string; ratio?: string; imageUrl?: string; model_id?: string }) => {
    return request<{ url: string }>('/ai/alibaba/image', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  createVideo: async (payload: {
    prompt: string;
    imageUrl?: string | null;
    duration?: number;
    ratio?: string;
    resolution?: string;
    model_id?: string;
  }) => {
    return request<{ id: string; status?: string }>('/ai/alibaba/video', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getVideoTask: async (taskId: string) => {
    return request<{ id: string; status?: string; content?: { video_url?: string } }>(
      `/ai/alibaba/video/${taskId}`
    );
  },
};

// AI 对话历史相关 API
export const chatHistoryAPI = {
  // 获取会话列表
  getSessions: async (limit: number = 50) => {
    return request<Array<{
      id: string;
      title: string;
      created_at: string;
      updated_at: string;
      message_count: number;
      last_message: string | null;
    }>>('/chat/sessions', {
      params: { limit },
    });
  },

  // 获取会话消息
  getSessionMessages: async (sessionId: string) => {
    return request<Array<{
      id: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
      created_at: string;
    }>>(`/chat/sessions/${sessionId}/messages`);
  },

  // 创建会话
  createSession: async (title?: string) => {
    return request<{ success: boolean; id: string }>('/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  },

  // 保存消息
  createMessage: async (sessionId: string, data: { role: 'user' | 'assistant' | 'system'; content: string }) => {
    return request<{ success: boolean; id: string }>(`/chat/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
